import React, { useEffect, useRef, useState } from 'react';

import { analyticsService } from '@common/analytics';
import { SvgIcon } from '@common/sprites';
import { Title } from '@barges/react';
import { CountdownCloseButton } from '@common/countdown_close_button/countdown_close_button';
import { useTranslation } from 'react-i18next';
import { PurchaseButtonWrapper } from '@app/components/purchase_flow';
import { usePurchaseFlowRenderProps } from '@common/config/purchaseFlowRenderHelpers';
import classNames from 'classnames';
import Const from '@app/config/const';
import { motion, AnimatePresence } from 'motion/react';
import classes from './classes.module.scss';
import { formatLocalCurrency } from '../../app/config/util';

type State = 'waiting' | 'processing' | 'done';

type Props = {
  show: boolean,
  closeAfter: number,
  duration: number,
  localAmount: string,
  currency: string,
  state: State,
  chatState: string,
  advisorId?: number,
  onClose: () => void,
  onButtonClicked: () => void
  onPurchaseCompleted: () => void;
  onPurchaseFailed: () => void;
  hideTopUpBanner: () => void;
};

export const QuickTopupBanner: React.FC<Props> = ({
  show, closeAfter, duration, localAmount, currency, state, onClose, onButtonClicked, onPurchaseCompleted, onPurchaseFailed, chatState, advisorId
}) => {
  const { renderIcon, renderText } = usePurchaseFlowRenderProps();
  const timer = useRef<NodeJS.Timeout>(null);
  const [timeLeft, setTimeLeft] = useState(closeAfter);
  const { t } = useTranslation(null, { keyPrefix: 'chat.topup_banner' });

  const renderButtonContent = () => {
    switch (state) {
      case 'processing':
        return (
          <Title fontStyle="content-medium" bold>
            {t('processing_btn_title')}
            {' '}
            <span className={ classes.dots } />
          </Title>
        );
      case 'done':
        return (
          <>
            <SvgIcon id="purchases_success" size={ 20 } />
            <Title fontStyle="content-medium" bold>{t('done_btn_title')}</Title>
          </>
        );
      default: return (
        <>
          <Title fontStyle="content-medium" bold>{t('add_btn_title', { count: duration })}</Title>
          <Title fontStyle="caption-2">{t('add_btn_subtitle', { amount: formatLocalCurrency(localAmount, currency) })}</Title>
        </>
      );
    }
  };

  useEffect(() => {
    if (!show) return () => {};

    setTimeLeft(closeAfter);

    timer.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev - 1 >= 0) return prev - 1;
        clearInterval(timer.current);
        return 0;
      });
    }, 1000);

    return () => clearInterval(timer.current);
  }, [show]);

  return (
    <AnimatePresence initial={ false }>
      {show && (
        <motion.div
          initial={ { height: 0, opacity: 0 } }
          animate={ { height: 'auto', opacity: 1 } }
          exit={ { height: 0, opacity: 0 } }
          transition={ { duration: 0.4, ease: 'easeInOut' } }
        >
          <div className={ classes.container }>
            <div className={ classes.titleAndIconContainer }>
              <div className={ classes.iconContainer }>
                <SvgIcon id="topup_banner_icon" className={ classes.icon } />
              </div>
              <div className={ classes.titleContainer }>
                <Title fontStyle="content-medium" bold>{t('title')}</Title>
                <div className={ classes.subtitle }>
                  <Title fontStyle="caption-1">{t('subtitle')}</Title>
                </div>
              </div>
            </div>
            <div className={ classes.btnWrapper }>
              <PurchaseButtonWrapper
                key="quick-top-up-btn"
                isContinue
                purchaseAmount={ undefined }
                onComplete={ onPurchaseCompleted }
                onError={ onPurchaseFailed }
                showErrorScreen={ false }
                onAnalyticsEvent={ (...args) => analyticsService.trackEvent(...args) }
                renderStatusPopup={ chatState !== Const.chatStates.started }
                renderIcon={ renderIcon }
                renderText={ renderText }
                purchaseType="quick_top_up"
                topupFlow="quick"
                disabled={ state !== 'waiting' }
                onClickHandler={ onButtonClicked }
                advisorId={ advisorId }
                className={ classNames(classes.btn, {
                  [classes.add]: state === 'waiting',
                  [classes.processing]: state === 'processing',
                  [classes.done]: state === 'done'
                }) }
              >
                {renderButtonContent()}
              </PurchaseButtonWrapper>
            </div>
            {!!timeLeft && state === 'waiting' && (
              <div className={ classes.closeIconContainer }>
                <CountdownCloseButton totalDuration={ closeAfter } timeLeft={ timeLeft } onClose={ onClose } />
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
